import React, { useEffect, ReactNode } from 'react';
import { Snackbar, Button, Theme, Slide, SnackbarCloseReason, SlideProps } from '@mui/material';
import MuiAlert from '@mui/lab/Alert';
import { useSelector, useDispatch } from 'react-redux';
import { app, getSnackMessage } from 'store';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle } from '@globalvisioninc/assets/icons';
import { fontFamily, lightGreen, primaryTextColor } from 'styles/vars';

export interface GVSnackMessage {
  message: string | ReactNode;
  type?: 'error' | 'success' | 'info';
  autoHideDuration?: number | null;
  yAlign?: 'bottom' | 'top';
  xAlign?: 'left' | 'right' | 'center';
  open?: boolean;
  /** When false there is no DISMISS button and Escape is ignored, leaving the custom action as the only way out. */
  dismissible?: boolean;
  customAction?: {
    label: string;
    onClick: () => void;
    /** 'link' sits inline with the message text; 'standalone' is set apart as the toast's own action. */
    variant?: 'link' | 'standalone';
  };
}

interface GVSnackbarProps {
  inline?: boolean; // render inside parent container rather than portal to body
}

/** Snackbar's own TransitionComponent so Slide does not overwrite the center `translateX(-50%)`. */
function SlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

const filledSuccess = {
  backgroundColor: lightGreen,
  padding: '8px 16px',
  '& .MuiAlert-icon': {
    padding: 0,
    marginRight: 12,
    opacity: 1,
  },
  '& .MuiAlert-message': {
    fontFamily,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '18px',
  },
};

const useStyles = makeStyles()((_theme: Theme) => {
  return {
    root: {
      zIndex: 10000,
      ' *': {
        color: primaryTextColor,
      },
      '& .MuiAlert-root': {
        alignItems: 'center',
      },
      '& .MuiAlert-message': {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
      },
      '& .MuiAlert-action': {
        alignItems: 'center',
        padding: 0,
        marginRight: 0,
      },
      '& .MuiAlert-filledSuccess': filledSuccess,
    },
    inlineContainer: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 16,
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      pointerEvents: 'none',
      ' *': {
        color: primaryTextColor,
      },
      '& .MuiAlert-message': {
        display: 'flex',
        alignItems: 'center',
        padding: 0,
      },
      '& .MuiAlert-root': {
        alignItems: 'center',
        pointerEvents: 'auto',
      },
      '& .MuiAlert-icon': {
        color: primaryTextColor,
      },
      '& .MuiAlert-filledSuccess': filledSuccess,
    },
    customActionButton: {
      cursor: 'pointer',
      color: 'inherit',
      fontWeight: '600',
    },
    linkAction: {
      textDecoration: 'underline',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
    standaloneAction: {
      // Sits on top of the button's own 16px inner padding.
      marginLeft: '36px',
    },
  };
});

const GVSnackbar = ({ inline }: GVSnackbarProps) => {
  const snackBarMessage = useSelector(getSnackMessage);
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const {
    message = '',
    autoHideDuration = snackBarMessage.type === 'error' ? 5000 : 3000,
    type = 'error',
    yAlign = 'bottom',
    xAlign = 'center',
    open = true,
    dismissible = true,
    customAction,
  } = snackBarMessage;

  const handleClose = (event?: Event | React.SyntheticEvent<any, Event>, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway' || (!dismissible && reason === 'escapeKeyDown')) {
      return;
    }
    dispatch(app.actions.getNextSnackMessage());
  };

  // Need to manually reset the timer if the message changes (in the event there are several messages in the snackbar queue)
  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (open && autoHideDuration !== null) {
      // Start or reset the timer whenever message, open, or duration changes
      timerId = setTimeout(() => {
        handleClose();
      }, autoHideDuration);
    }
    // Clean up the timer when the component unmounts or when Snackbar is closed manually
    return () => clearTimeout(timerId);
  }, [message, open, autoHideDuration]);

  const snackbarContent = (
    <MuiAlert
      action={
        dismissible ? (
          <Button onClick={handleClose} color="inherit" size="small">
            DISMISS
          </Button>
        ) : undefined
      }
      icon={
        type === 'success' ? (
          <CheckCircle size={22} color={primaryTextColor} strokeWidth={1.833} aria-hidden style={{ opacity: 0.9 }} />
        ) : undefined
      }
      severity={type}
      color={type}
      elevation={6}
      variant="filled"
    >
      {message}
      {customAction && (
        <Button
          onClick={() => {
            customAction.onClick();
            handleClose();
          }}
          color="inherit"
          size="small"
          className={cx(
            classes.customActionButton,
            customAction.variant === 'standalone' ? classes.standaloneAction : classes.linkAction,
          )}
        >
          {customAction.label}
        </Button>
      )}
    </MuiAlert>
  );

  if (inline) {
    return (
      <Slide direction="up" in={open && !!message} mountOnEnter unmountOnExit>
        <div className={classes.inlineContainer}>{snackbarContent}</div>
      </Slide>
    );
  }

  return (
    <Snackbar
      className={classes.root}
      anchorOrigin={{ vertical: yAlign, horizontal: xAlign }}
      open={open && !!message}
      autoHideDuration={autoHideDuration === null ? undefined : autoHideDuration}
      onClose={handleClose}
      TransitionComponent={SlideUp}
    >
      {snackbarContent}
    </Snackbar>
  );
};

export default GVSnackbar;
